/**
 * Browser alerts: this browser registered as a push device, beside the phones.
 *
 * The backend sends every alert push to each of the account's FCM registration tokens
 * (`device_tokens`, api_reference.md §11), and a browser's token is the same shape as a phone's.
 * So turning alerts on here is: ask the browser's permission, register the service worker
 * (`public/push-sw.js`), have Firebase mint a token for it, and POST that token with
 * `platform: 'web'`. The push job's gates — the account's push switch and alert hours — apply to
 * this token exactly as to a handset.
 *
 * Permission is asked only from a click, never on load: once a visitor blocks notifications the
 * browser never shows the prompt again, and only the visitor can undo that from the site settings.
 * `pushState()` tells the Account page which of those situations it is in.
 *
 * The token registered from this browser is kept in localStorage so turning off and signing out
 * can remove exactly that row; tokens rotate, so every signed-in load re-registers (`refreshPush`).
 */
import { api, call } from '@/api/client';
import { isApiError } from '@/api/errors';
import { env, pushConfigured } from '@/lib/env';

const SW_URL = '/push-sw.js';
const TOKEN_KEY = 'sixone8.pushToken';

export type PushState = 'unsupported' | 'unconfigured' | 'denied' | 'off' | 'on';

/** Bound on everything after permission is granted; the browser's own steps can stall without failing. */
const REGISTER_TIMEOUT_MS = 30_000;

export class PushPermissionError extends Error {
  constructor(public readonly permission: NotificationPermission) {
    super(permission === 'denied' ? 'Notifications are blocked for this site.' : 'The notification prompt was dismissed.');
  }
}

/**
 * The browser granted permission but could not subscribe. Brave and Vivaldi ship with no push
 * service (Brave's is a setting, off by default), and private windows have none, so
 * `pushManager.subscribe` rejects there — a message the visitor can act on, not a server error.
 */
export class PushSetupError extends Error {
  constructor(timedOut = false) {
    super(
      timedOut
        ? 'Your browser did not finish setting up alerts. Try again, or use Chrome, Edge, Firefox or Safari.'
        : 'This browser could not subscribe to push messages. Private windows cannot receive them; in Brave, turn on "Use Google services for push messaging" in Settings → Privacy and security.',
    );
  }
}

export function pushSupported(): boolean {
  return (
    typeof window !== 'undefined' &&
    'Notification' in window &&
    'serviceWorker' in navigator &&
    'PushManager' in window
  );
}

/** iOS and iPadOS offer push only to a site added to the Home Screen; Safari in a tab has no PushManager. */
export function needsHomeScreen(): boolean {
  if (pushSupported()) return false;
  const ua = navigator.userAgent;
  const apple = /iPhone|iPad|iPod/.test(ua) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
  const standalone =
    window.matchMedia?.('(display-mode: standalone)').matches ||
    (navigator as Navigator & { standalone?: boolean }).standalone === true;
  return apple && !standalone;
}

export function pushState(): PushState {
  if (!pushSupported()) return 'unsupported';
  if (!pushConfigured) return 'unconfigured';
  if (Notification.permission === 'denied') return 'denied';
  if (Notification.permission === 'granted' && readToken()) return 'on';
  return 'off';
}

function readToken(): string | null {
  try {
    return localStorage.getItem(TOKEN_KEY);
  } catch {
    return null;
  }
}

function writeToken(token: string | null) {
  try {
    if (token) localStorage.setItem(TOKEN_KEY, token);
    else localStorage.removeItem(TOKEN_KEY);
  } catch {
    // Without storage the registration still works for this load; the next one re-registers.
  }
}

async function messaging() {
  const [{ firebaseApp }, m] = await Promise.all([import('@/auth/firebase'), import('firebase/messaging')]);
  if (!(await m.isSupported())) throw new Error('This browser cannot receive push messages.');
  return { m, instance: m.getMessaging(firebaseApp()) };
}

async function workerRegistration(): Promise<ServiceWorkerRegistration> {
  const reg = await navigator.serviceWorker.register(SW_URL, { scope: '/' });
  await navigator.serviceWorker.ready;
  return reg;
}

let registering: Promise<string> | null = null;

/** Mint (or re-read) this browser's token and register it; one in flight at a time. */
function registerToken(): Promise<string> {
  if (!registering) {
    registering = (async () => {
      const { m, instance } = await messaging();
      const token = await m.getToken(instance, {
        vapidKey: env.firebase.vapidKey,
        serviceWorkerRegistration: await workerRegistration(),
      });
      await call(api.POST('/api/v1/device-tokens', { body: { platform: 'web', push_token: token } }));
      writeToken(token);
      return token;
    })().finally(() => {
      registering = null;
    });
  }
  return registering;
}

/**
 * From a click: asks permission. Throws `PushPermissionError` on block or dismiss.
 *
 * Not bounded: Edge (and Chrome, for sites it judges noisy) may show a "quiet" request — only a bell
 * in the address bar, no popup — and the promise stays pending until the visitor answers it there.
 * The caller shows where to look instead of timing out on a prompt the visitor may still accept.
 */
export async function requestPushPermission(): Promise<void> {
  const permission = await Notification.requestPermission();
  if (permission !== 'granted') throw new PushPermissionError(permission);
}

/** After `requestPushPermission`: registers this browser. Throws `PushSetupError` when the browser cannot. */
export async function enablePush(): Promise<void> {
  let timer: ReturnType<typeof setTimeout> | undefined;
  const timeout = new Promise<never>((_, reject) => {
    timer = setTimeout(() => reject(new PushSetupError(true)), REGISTER_TIMEOUT_MS);
  });
  try {
    await Promise.race([registerToken(), timeout]);
  } catch (e) {
    if (e instanceof PushSetupError || isApiError(e)) throw e;
    throw new PushSetupError();
  } finally {
    clearTimeout(timer);
  }
}

/** Every signed-in load while alerts are on: tokens rotate, and the server must hold the live one. */
export async function refreshPush(): Promise<void> {
  if (pushState() !== 'on') return;
  try {
    await registerToken();
  } catch {
    // Left for the next load; the previous token stays registered meanwhile.
  }
}

async function unregister(): Promise<void> {
  const token = readToken();
  writeToken(null);
  if (token) {
    try {
      await call(api.DELETE('/api/v1/device-tokens/{push_token}', { params: { path: { push_token: token } } }));
    } catch {
      // Already gone (pruned, or re-pointed to another account) — nothing left to remove.
    }
  }
  try {
    const { m, instance } = await messaging();
    await m.deleteToken(instance);
  } catch {
    // The server row is what stops delivery; the local subscription is tidied when it can be.
  }
}

/** Turn alerts off in this browser. The permission stays granted, so turning on again needs no prompt. */
export const disablePush = unregister;

/** Sign-out: this browser must not keep receiving the account's alerts. Runs before the session ends. */
export async function forgetPushOnSignOut(): Promise<void> {
  if (!readToken()) return;
  await unregister();
}

export interface PushMessage {
  type: string;
  body: string;
}

/** Messages the service worker posts to open tabs when a push arrives. */
export function onPushMessage(cb: (message: PushMessage) => void): () => void {
  if (typeof navigator === 'undefined' || !('serviceWorker' in navigator)) return () => {};
  const handler = (e: MessageEvent) => {
    const d = e.data as { source?: string; type?: string; body?: string } | null;
    if (d?.source === 'sixone8-push') cb({ type: d.type ?? 'alerts', body: d.body ?? '' });
  };
  navigator.serviceWorker.addEventListener('message', handler);
  return () => navigator.serviceWorker.removeEventListener('message', handler);
}

/**
 * Fires when the site's notification permission changes — the visitor allowing it again from the
 * address bar after a block — where the browser reports that; a no-op elsewhere.
 */
export function onPermissionChange(cb: () => void): () => void {
  if (typeof navigator === 'undefined' || !navigator.permissions?.query) return () => {};
  let status: PermissionStatus | null = null;
  let cancelled = false;
  navigator.permissions
    .query({ name: 'notifications' })
    .then((st) => {
      if (cancelled) return;
      status = st;
      st.addEventListener('change', cb);
    })
    .catch(() => undefined);
  return () => {
    cancelled = true;
    status?.removeEventListener('change', cb);
  };
}
